import React from 'react';
import { useSelector } from 'react-redux';
import { radioPlayerPlayLive } from '../../../../../utils/radioPlayerEvents';
import type { PublicScheduleItem } from '../../../../../interfaces/schedule';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

type Props = {
  item: PublicScheduleItem;
  isNowPlaying?: boolean;
  stationId: string;
  showNowButton?: boolean;
  isActive?: boolean;
};

function formatHourParts(value: string) {
  const [hours = '00', minutes = '00'] = value.split(':');

  return {
    hour: String(Number(hours)),
    suffix: 'h',
    minutes: minutes === '00' ? '' : minutes,
  };
}

export default function ScheduleRow({
  item,
  isNowPlaying = false,
  stationId,
  showNowButton = false,
  isActive = false,
}: Props) {
  const [isOpen, setIsOpen] = React.useState(false);

  const currentStationId = useSelector(
    (state: any) => state.player?.currentStationId ?? null,
  ) as string | null;

  const isPlaying = useSelector((state: any) => !!state.player?.isPlaying);
  const mode = useSelector((state: any) => state.player?.mode ?? 'live');
  const isLiveMode = mode !== 'vod';

  const isCurrentStationPlaying =
    isPlaying && isLiveMode && currentStationId === stationId;

  const handlePlayLive = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    radioPlayerPlayLive(stationId, {
      origin: 'ScheduleRow',
      analytics: {
        event: 'audio_play',
        section: 'schedule.grid',
        contentType: 'radio',
        stationId,
        label: 'Ouvir rádio agora',
      },
    });
  };

  const toggleOpen = () => setIsOpen((prev) => !prev);
  const start = formatHourParts(item.startTime);

  const programUrl = item.programUrl || '';

  return (
    <article
      className={`schedule-row ${
        isOpen ? 'is-open' : ''
      } ${isNowPlaying ? 'is-now-playing' : ''} ${isActive ? 'is-active' : ''}`}
    >
      <div className="schedule-row__summary">
        <div
          className="schedule-row__main"
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={toggleOpen}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleOpen();
            }
          }}
        >
          <div className="schedule-row__time">
            <div className="schedule-row__headTime">
              <strong className="schedule-row__time-value">
                <span className="schedule-row__time-hour">{start.hour}</span>
                <span className="schedule-row__time-suffix">
                  {start.suffix}
                </span>
                {start.minutes ? (
                  <span className="schedule-row__time-minutes">
                    {start.minutes}
                  </span>
                ) : null}
              </strong>

              <span className="schedule-row__timezone">
                horário
                <br />
                de Brasília
              </span>
            </div>

            {showNowButton && isNowPlaying ? (
              <button
                type="button"
                className={`schedule-row__listen-button ${
                  isCurrentStationPlaying ? 'is-playing' : ''
                }`}
                onClick={handlePlayLive}
                aria-label="Ouvir rádio agora"
                title="Ouvir rádio agora"
              >
                <span className="schedule-row__listen-dot" aria-hidden="true" />
                <span>{isCurrentStationPlaying ? 'tocando' : 'agora'}</span>
              </button>
            ) : null}
          </div>

          <div className="schedule-row__content">
            <h3 className="schedule-row__title">
              {programUrl ? (
                <UniversalLink
                  href={programUrl}
                  className="schedule-row__title-link"
                  title={`Acessar página do programa ${item.title}`}
                  aria-label={`Acessar página do programa ${item.title}`}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {item.title}
                </UniversalLink>
              ) : (
                item.title
              )}
            </h3>

            <div className="schedule-row__badges">
              {item.isLive ? (
                <span className="schedule-row__badge schedule-row__badge--live">
                  ao vivo
                </span>
              ) : null}

              {item.isRerun ? (
                <span className="schedule-row__badge schedule-row__badge--rerun">
                  reprise
                </span>
              ) : null}

              {item.status === 'cancelled' ? (
                <span className="schedule-row__badge schedule-row__badge--cancelled">
                  cancelado
                </span>
              ) : null}

              {isNowPlaying ? (
                <span className="schedule-row__badge schedule-row__badge--playing">
                  no ar
                </span>
              ) : null}
            </div>

            {isOpen ? (
              <>
                {item.description ? (
                  <p className="schedule-row__description">
                    {item.description}
                  </p>
                ) : null}

                <div className="schedule-row__meta">
                  <span>Duração: {item.durationMinutes} min</span>
                  <span>Termina às {item.endTime}</span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className="schedule-row__toggle"
          onClick={toggleOpen}
          aria-label={isOpen ? 'Fechar detalhes' : 'Abrir detalhes'}
          aria-expanded={isOpen}
        >
          <span
            className={`schedule-row__icon ${isOpen ? 'is-open' : ''}`}
            aria-hidden="true"
          >
            {isOpen ? '−' : '+'}
          </span>
        </button>
      </div>
    </article>
  );
}
